import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { getAttribution } from "../../lib/attribution";
import { packageApi } from "../../lib/packageApi";
import {
  alwaysIds,
  defaultAnswers,
  listedAlways,
  preselectedIds,
  requiresBookkeepingIds,
  skipsOnboardingIds,
} from "../../lib/packageLogic";
import { buildJourney, idsByName, journeyMissing, journeyPayload, journeyProgress } from "../../lib/pricingJourney";
import { helpEnabled, helpQuestions, recommend } from "../../lib/recommend";
import { pricing as trackPricing, trackEvent } from "../../lib/analytics";
import { hasPrice } from "../package/QuotePanel";
import PackageContact from "../package/PackageContact";
import DiscoveryBooking from "../package/DiscoveryBooking";
import JourneyProgress from "./JourneyProgress";
import ServiceCards from "./ServiceCards";
import HelpMeChoose, { HelpChoicePanel } from "./HelpMeChoose";
import Recommendations from "./Recommendations";
import QuestionGroups from "./QuestionGroups";
import ServiceStage from "./ServiceStage";
import PriceStage from "./PriceStage";
import WalkawayOffer from "./WalkawayOffer";
import SummaryPanel from "./SummaryPanel";

const STORE = "cass_pricing_journey";
const KEEP_DAYS = 30;
const STAGES = ["Services", "Your business", "Service details", "Your price", "Next steps"];

// Answers and selections only — never a name, an email address or a phone number.
// Thirty days, then it is gone; sending an enquiry clears it immediately.
function restore() {
  try {
    const s = JSON.parse(localStorage.getItem(STORE) || "null");
    // Nothing worth restoring unless they chose something — or asked to talk,
    // which is a position of its own.
    if (!s?.selected?.length && !s?.directCall) return null;
    const age = Date.now() - (s.savedAt || 0);
    if (age > KEEP_DAYS * 24 * 60 * 60 * 1000) {
      localStorage.removeItem(STORE);
      return null;
    }
    return s;
  } catch {
    return null;
  }
}

export default function PricingJourney({ cfg = {} }) {
  const b = cfg.builder || {};
  const saved = useMemo(restore, []);

  const [cat, setCat] = useState(null);
  const [unavailable, setUnavailable] = useState(false);
  const [stage, setStage] = useState(saved?.directCall && saved?.stage === 5 ? 5 : Math.min(saved?.stage || 1, 4));
  const [serviceIndex, setServiceIndex] = useState(saved?.serviceIndex || 0);
  const [selected, setSelected] = useState(saved?.selected || []);
  const [answers, setAnswers] = useState(saved?.answers || {});
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [quoteError, setQuoteError] = useState("");
  const [serverMissing, setServerMissing] = useState([]);
  const [showMissing, setShowMissing] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [contact, setContact] = useState(null);
  // "" = the two ways in, "help" = the three questions, "manual" = the service list.
  const [pickMode, setPickMode] = useState(saved?.pickMode || "");
  const [helpAnswers, setHelpAnswers] = useState(saved?.helpAnswers || {});
  const [recommended, setRecommended] = useState(saved?.recommended || []);
  // They asked to talk rather than choose: the booking comes first, not a form.
  const [directCall, setDirectCall] = useState(Boolean(saved?.directCall));
  // The one-time offer of a call to somebody leaving the price screen. Once it's
  // been seen or dismissed it never comes back, refresh or not.
  const [showOffer, setShowOffer] = useState(false);
  const [offerDone, setOfferDone] = useState(Boolean(saved?.offerDone));
  const top = useRef(null);

  // Services CASS has chosen not to offer through the website at all.
  const excluded = useMemo(() => cfg.journey_excluded_service_ids || [], [cfg]);
  const services = useMemo(
    () => (cat?.services || []).filter((s) => !excluded.includes(s.service_id)),
    [cat, excluded],
  );

  useEffect(() => {
    packageApi.catalogue()
      .then((c) => {
        setCat(c);
        if (!saved) {
          setSelected(preselectedIds(c.services));
          setAnswers(defaultAnswers(c.services));
        }
      })
      .catch(() => setUnavailable(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A resumed journey can't keep a service the website no longer offers.
  useEffect(() => {
    if (!cat || excluded.length === 0) return;
    setSelected((prev) => (prev.some((id) => excluded.includes(id)) ? prev.filter((id) => !excluded.includes(id)) : prev));
  }, [cat, excluded]);

  useEffect(() => {
    localStorage.setItem(
      STORE,
      JSON.stringify({ v: 1, selected, answers, stage, serviceIndex, pickMode, helpAnswers, recommended, directCall, offerDone, savedAt: Date.now() }),
    );
  }, [selected, answers, stage, serviceIndex, pickMode, helpAnswers, recommended, directCall, offerDone]);

  // ---------------------------------------------------------------- policy
  // Mandatory services are priced like any other — the visitor just can't remove
  // them. Work that doesn't take anyone on as a client carries no onboarding fee
  // when it's the whole basket.
  const allAlways = useMemo(() => alwaysIds(services), [services]);
  const noOnboardingIds = useMemo(() => skipsOnboardingIds(services), [services]);
  const onboardingApplies = useMemo(
    () => selected.length === 0 || selected.some((id) => !noOnboardingIds.includes(id)),
    [selected, noOnboardingIds],
  );
  const mandatory = useMemo(() => (onboardingApplies ? allAlways : []), [onboardingApplies, allAlways]);
  const included = useMemo(() => [...new Set([...selected, ...mandatory])], [selected, mandatory]);
  const standardItems = useMemo(() => (onboardingApplies ? listedAlways(services) : []), [services, onboardingApplies]);

  const bkId = cfg.bookkeeping_service_id || "";
  const bkKnown = useMemo(() => services.some((s) => s.service_id === bkId), [services, bkId]);
  const bkName = useMemo(() => services.find((s) => s.service_id === bkId)?.name || "Monthly Bookkeeping", [services, bkId]);
  const bkIncluded = !bkId || !bkKnown || included.includes(bkId);
  const restrictedIds = useMemo(() => requiresBookkeepingIds(services), [services]);
  const lockedIds = bkIncluded ? [] : restrictedIds;
  const conflictIds = useMemo(
    () => (bkIncluded ? [] : restrictedIds.filter((id) => selected.includes(id))),
    [bkIncluded, restrictedIds, selected],
  );
  const blocked = conflictIds.length > 0;

  // Bookkeeping when something they've chosen depends on it — marked as required
  // rather than recommended, so the two reasons a service is in the list stay
  // clearly apart.
  const requiredIds = useMemo(
    () => (bkId && bkIncluded && selected.some((id) => restrictedIds.includes(id)) ? [bkId] : []),
    [bkId, bkIncluded, selected, restrictedIds],
  );
  const requiredNames = useMemo(
    () => [
      ...standardItems.map((s) => s.name),
      ...requiredIds.map((id) => services.find((s) => s.service_id === id)?.name).filter(Boolean),
    ],
    [standardItems, requiredIds, services],
  );

  const mirrored = useMemo(() => cfg.bookkeeping_mirror_answers || [], [cfg]);
  useEffect(() => {
    if (mirrored.length === 0 || !cat) return;
    const fix = mirrored.filter((k) => answers[k] !== bkIncluded);
    if (fix.length === 0) return;
    setAnswers((prev) => ({ ...prev, ...Object.fromEntries(fix.map((k) => [k, bkIncluded])) }));
  }, [bkIncluded, cat, answers, mirrored]);

  // Questions CASS answers on the visitor's behalf — held at their set value even
  // if an older saved journey carries something else.
  const fixedAnswers = useMemo(() => cfg.fixed_answers || {}, [cfg]);
  useEffect(() => {
    if (!cat) return;
    const wrong = Object.entries(fixedAnswers).filter(([key, rule]) => answers[key] !== rule.value);
    if (wrong.length === 0) return;
    setAnswers((prev) => ({ ...prev, ...Object.fromEntries(wrong.map(([key, rule]) => [key, rule.value])) }));
  }, [cat, answers, fixedAnswers]);

  const addBookkeeping = (alsoAdd = "") => {
    setSelected((prev) => [...new Set([...prev, bkId, ...(alsoAdd ? [alsoAdd] : [])])]);
    setPrompt("");
    setShowMissing(false);
    setServerMissing([]);
  };

  // ---------------------------------------------------------------- journey
  const journey = useMemo(() => {
    const j = buildJourney(services, included, answers, cfg, mirrored);
    // Mandatory services last: a visitor's own choices should come first, so the
    // journey doesn't open on a fee they never picked.
    return { ...j, sections: [...j.sections].sort((a, b) => Number(mandatory.includes(a.service.service_id)) - Number(mandatory.includes(b.service.service_id))) };
  }, [services, included, answers, cfg, mirrored, mandatory]);
  const sections = journey.sections;
  const payload = useMemo(() => journeyPayload(journey, answers), [journey, answers]);
  const localMissing = useMemo(() => journeyMissing(journey, answers), [journey, answers]);
  const progress = useMemo(() => journeyProgress(journey), [journey]);
  const nameToId = useMemo(() => idsByName(services), [services]);
  const notes = useMemo(
    () => Object.fromEntries(services.map((s) => [s.service_id, s.display?.note || ""])),
    [services],
  );

  const missingKeys = useMemo(
    () => [...new Set([...(showMissing ? localMissing : []), ...serverMissing].map((m) => m.key))],
    [localMissing, serverMissing, showMissing],
  );

  const summaryServices = useMemo(
    () => sections.map((sec) => ({ service_id: sec.service.service_id, name: sec.service.name, complete: sec.complete })),
    [sections],
  );

  // ---------------------------------------------------------------- pricing
  const runQuote = useCallback(async (attempt = 0) => {
    setQuoting(true);
    try {
      const data = await packageApi.quote({
        services: included.map((id) => ({ service_id: id, included: true })),
        answers: payload,
      });
      setQuote(
        data.status === "bookkeeping_not_qualifying" || data.eligibility?.bookkeeping === false
          ? { ...data, status: "consultation" }
          : data,
      );
      setQuoteError("");
      setServerMissing([]);
      if (cat?.pricing_version && data.pricing_version && data.pricing_version !== cat.pricing_version) {
        packageApi.catalogue().then(setCat).catch(() => {});
      }
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 422 && detail?.missing) {
        setServerMissing(detail.missing);
        setShowMissing(true);
        setQuote(null);
        setQuoteError("");
      } else if (status === 429 && attempt < 1) {
        setTimeout(() => runQuote(attempt + 1), 2000);
        return;
      } else {
        setQuote(null);
        setQuoteError(
          status === 429
            ? "You're going a little faster than our pricing engine. Give it a moment and change an answer to try again."
            : "Our pricing service isn't responding at the moment. Nothing you've entered is lost — try again in a minute, or send us your details and we'll price it by hand.",
        );
      }
    } finally {
      setQuoting(false);
    }
  }, [included, payload, cat]);

  // Debounced, so changing an answer and changing it back costs one call.
  useEffect(() => {
    if (!cat || selected.length === 0 || stage > 4) return;
    if (blocked || localMissing.length > 0) { setQuote(null); return; }
    const t = setTimeout(() => runQuote(), 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cat, included, payload, stage, blocked]);

  // A resumed journey can hold a combination the rule no longer allows.
  useEffect(() => {
    if (blocked && stage > 1) setStage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocked]);

  // ---------------------------------------------------------------- tracking
  // One event per genuine milestone, each fired once per journey, so nothing here
  // can inflate a conversion count.
  const chosenCount = included.filter((id) => !mandatory.includes(id)).length;
  const chosenCountRef = useRef(0);
  useEffect(() => { chosenCountRef.current = chosenCount; }, [chosenCount]);

  const openedTracked = useRef(false);
  const startedTracked = useRef(false);
  const furthest = useRef(stage);
  const [furthestStage, setFurthestStage] = useState(stage);
  const servicesTracked = useRef(false);
  const businessTracked = useRef(false);
  const sectionsTracked = useRef(new Set());
  const quoteTracked = useRef("");
  const completedTracked = useRef(false);

  useEffect(() => {
    if (openedTracked.current) return;
    openedTracked.current = true;
    trackPricing.opened({ page_path: window.location.pathname, resumed: Boolean(saved) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (stage <= furthest.current) return;
    furthest.current = stage;
    setFurthestStage(stage);
    trackPricing.step({ step_number: stage, step_name: STAGES[stage - 1] });
    // "Completed" is reported when a price is actually shown, not merely when the
    // price stage is reached — somebody who jumps straight to a Discovery Call
    // never saw a price and shouldn't count as having finished the tool.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  useEffect(() => {
    const onLeave = () => {
      if (!startedTracked.current || furthest.current >= 5) return;
      trackPricing.abandoned({
        last_step_number: furthest.current,
        last_step_name: STAGES[furthest.current - 1],
        services_selected: chosenCountRef.current,
      });
    };
    window.addEventListener("pagehide", onLeave);
    return () => {
      window.removeEventListener("pagehide", onLeave);
      onLeave();
    };
  }, []);

  // The price, reported where it is actually shown and only when it changes.
  useEffect(() => {
    if (stage < 4 || !quote) return;
    const key = `${quote.status}|${quote.totals?.monthly_recurring || 0}|${included.length}`;
    if (quoteTracked.current === key) return;
    quoteTracked.current = key;
    trackPricing.quote({
      status: quote.status,
      monthly_net: quote.totals?.monthly_recurring || 0,
      pricing_version: quote.pricing_version || "",
      services_selected: included.length,
      selection_mode: selectionMode.current,
    });
    if (quote.status === "consultation") trackEvent("package_consultation_shown", {});
    if (!completedTracked.current) {
      completedTracked.current = true;
      trackPricing.completed({ services_selected: chosenCountRef.current, selection_mode: selectionMode.current });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, quote]);

  // ---------------------------------------------------------------- movement
  const go = useCallback((n, index) => {
    if (index !== undefined) setServiceIndex(index);
    setStage(n);
    setShowMissing(false);
    requestAnimationFrame(() => {
      const el = top.current;
      if (!el) return;
      const y = el.getBoundingClientRect().top + window.scrollY - 120;
      window.scrollTo({ top: Math.max(y, 0), behavior: "smooth" });
    });
  }, []);

  const toggle = (id) => {
    if (!startedTracked.current) {
      startedTracked.current = true;
      trackPricing.started({ entry_step: STAGES[stage - 1] });
    }
    if (!bkIncluded && restrictedIds.includes(id) && !selected.includes(id)) {
      setPrompt(id);
      return;
    }
    setPrompt("");
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    setShowMissing(false);
    setServerMissing([]);
  };

  const onAnswer = (key, value) => {
    setAnswers((a) => ({ ...a, [key]: value }));
    setServerMissing((m) => m.filter((x) => x.key !== key));
  };

  const firstSectionWithQuestions = () => {
    const i = sections.findIndex((s) => s.groups.length > 0);
    return i < 0 ? 0 : i;
  };

  // A refresh keeps the journey, so the mode has to come from what was saved —
  // pickMode is "manual" by then, because the questions are behind them.
  const selectionMode = useRef(
    saved?.recommended?.length || Object.keys(saved?.helpAnswers || {}).length ? "help_me_choose" : "manual",
  );
  const recommendationsAccepted = useRef(false);
  const helpCompletedTracked = useRef(Boolean(saved?.recommended?.length));

  // Where they've got to, so the admin can see which question loses people. One
  // row per journey, holding a stage and a question key — no answers, no price,
  // nothing personal. Only once they've actually started choosing.
  const point = useMemo(() => {
    if (stage === 2) return localMissing.find((m) => m.stage === "business") || null;
    if (stage === 3) return localMissing.find((m) => m.stage === "service" && m.index === serviceIndex) || null;
    return null;
  }, [stage, serviceIndex, localMissing]);

  // The question in front of them, and how long it has been there. The timing is
  // reported when they move on, so the admin can see which questions make people
  // hesitate rather than only which ones they abandon.
  const current = useRef({ key: "", label: "", stage: "", at: Date.now() });

  const report = useCallback((next) => {
    const prev = current.current;
    const seconds = (Date.now() - prev.at) / 1000;
    current.current = { key: next.question_key || "", label: next.question_label || "", stage: next.stage_name || "", at: Date.now() };
    packageApi.progress({
      ...next,
      previous_key: prev.key,
      previous_label: prev.label,
      previous_stage_name: prev.stage,
      seconds: prev.key ? Number(seconds.toFixed(1)) : 0,
    });
  }, []);

  useEffect(() => {
    if (!startedTracked.current) return;
    const t = setTimeout(() => {
      report({
        stage,
        stage_name: STAGES[stage - 1] || "",
        question_key: point?.key || "",
        question_label: point?.label || "",
        service_count: chosenCountRef.current,
        mode: selectionMode.current,
      });
    }, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, serviceIndex, point?.key, pickMode, selected.length]);

  // The last question they were on when they close the tab counts too, otherwise
  // the question that loses people would never record a time.
  useEffect(() => {
    const onHide = () => {
      if (!startedTracked.current || !current.current.key) return;
      report({
        stage,
        stage_name: STAGES[stage - 1] || "",
        question_key: current.current.key,
        question_label: current.current.label,
        service_count: chosenCountRef.current,
        mode: selectionMode.current,
      });
    };
    window.addEventListener("pagehide", onHide);
    return () => window.removeEventListener("pagehide", onHide);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  // ------------------------------------------------- the offer of a call
  const offer = cfg.walkaway_offer || {};
  useEffect(() => {
    if (offerDone || offer.enabled === false) return;
    if (stage !== 4 || !quote || !hasPrice(quote)) return;
    let idle;
    const open = () => {
      setShowOffer(true);
      setOfferDone(true);
      trackEvent("pricing_walkaway_offer_shown", { monthly_net: quote.totals?.monthly_recurring || 0 });
    };
    // Desktop: the cursor heading for the address bar. Everywhere: a long pause
    // with the price on screen and nothing happening.
    const onOut = (e) => { if (e.clientY <= 0) open(); };
    const reset = () => { clearTimeout(idle); idle = setTimeout(open, 45000); };
    document.addEventListener("mouseout", onOut);
    ["pointerdown", "keydown", "scroll"].forEach((n) => window.addEventListener(n, reset, { passive: true }));
    reset();
    return () => {
      clearTimeout(idle);
      document.removeEventListener("mouseout", onOut);
      ["pointerdown", "keydown", "scroll"].forEach((n) => window.removeEventListener(n, reset));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, quote, offerDone, offer.enabled]);

  const bookFromOffer = () => {
    trackEvent("pricing_walkaway_offer_accepted", { monthly_net: quote?.totals?.monthly_recurring || 0 });
    setShowOffer(false);
    setDirectCall(true);
    go(5);
  };

  const dismissOffer = () => {
    trackEvent("pricing_walkaway_offer_dismissed", {});
    setShowOffer(false);
  };


  const startHelp = () => {
    selectionMode.current = "help_me_choose";
    setPickMode("help");
    trackEvent("pricing_help_started", {});
    if (!startedTracked.current) {
      startedTracked.current = true;
      trackPricing.started({ entry_step: STAGES[0] });
    }
  };

  const finishHelp = (finalAnswers) => {
    const given = finalAnswers || helpAnswers;
    setHelpAnswers(given);
    const { ids } = recommend(given, cfg, services);
    const previous = recommended;
    setRecommended(ids);
    // Their suggestion, pre-ticked and theirs to change. Anything they ticked
    // themselves stays; anything only there because of an earlier answer goes, so
    // changing "do you run payroll?" to No actually removes Payroll.
    setSelected((prev) => [...new Set([...prev.filter((id) => !previous.includes(id)), ...ids])]);
    setPickMode("manual");
    setShowMissing(false);
    setServerMissing([]);
    if (!helpCompletedTracked.current) {
      helpCompletedTracked.current = true;
      trackEvent("pricing_help_completed", {
        business_type: given.business_type || "",
        help_focus: Array.isArray(given.focus) ? given.focus.join("|") : given.focus || "",
        payroll_answer: given.payroll || "",
      });
      trackEvent("pricing_recommendations_viewed", { services_recommended: ids.length });
    }
    go(1);
  };

  const helpDiscovery = () => {
    trackEvent("pricing_help_discovery_call", { answered: Object.keys(helpAnswers).length });
    setDirectCall(true);
    go(5);
  };

  const leaveServices = () => {
    if (!servicesTracked.current) {
      servicesTracked.current = true;
      trackEvent("pricing_services_selected", { services_selected: chosenCount, selection_mode: selectionMode.current });
    }
    if (recommended.length > 0 && !recommendationsAccepted.current) {
      recommendationsAccepted.current = true;
      trackEvent("pricing_recommendations_accepted", {
        services_recommended: recommended.length,
        services_kept: recommended.filter((id) => included.includes(id)).length,
        services_selected: chosenCount,
      });
    }
    // Nothing shared to ask about: straight to the first service.
    if (journey.business.questions.length === 0) go(3, firstSectionWithQuestions());
    else go(2);
  };

  const leaveBusiness = () => {
    const outstanding = journey.business.missing;
    if (outstanding.length > 0) {
      setShowMissing(true);
      jumpTo(outstanding[0]);
      return;
    }
    if (!businessTracked.current) {
      businessTracked.current = true;
      trackEvent("pricing_business_completed", { services_selected: chosenCount });
    }
    go(3, firstSectionWithQuestions());
  };

  const leaveSection = () => {
    const sec = sections[serviceIndex];
    if (sec && sec.missing.length > 0) {
      setShowMissing(true);
      jumpTo(sec.missing[0]);
      return;
    }
    if (sec && !sectionsTracked.current.has(sec.service.service_id)) {
      sectionsTracked.current.add(sec.service.service_id);
      trackEvent("pricing_section_completed", {
        service_name: sec.service.name,
        section_number: serviceIndex + 1,
        sections_total: sections.length,
      });
    }
    // The next service in order, not the next unanswered one. A service whose
    // questions all sit behind a "do you want this?" that defaults to No counts as
    // complete, so skipping ahead walked straight past VAT and Payroll and priced
    // them at zero without the visitor ever seeing them.
    const next = sections.findIndex((s, i) => i > serviceIndex && s.groups.length > 0);
    if (next >= 0) { go(3, next); return; }
    if (localMissing.length > 0) {
      setShowMissing(true);
      const first = localMissing[0];
      if (first.stage === "business") { go(2); return; }
      go(3, first.index);
      return;
    }
    go(4);
  };

  const jumpTo = (key) => {
    requestAnimationFrame(() => {
      const el = document.getElementById(`pj-field-${key}`);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      // The answer itself, not the help ⓘ that sits beside the question title.
      const control = el.querySelector(`[data-testid="pricing-q-${key}"]`);
      (control?.tagName === "DIV" ? control.querySelector("button") : control)?.focus({ preventScroll: true });
    });
  };

  const startOver = () => {
    if (!window.confirm("Start again with a blank form? Everything you've entered will be cleared.")) return;
    localStorage.removeItem(STORE);
    setAnswers(defaultAnswers(services));
    setSelected(preselectedIds(services));
    setQuote(null);
    setQuoteError("");
    setServerMissing([]);
    setShowMissing(false);
    setPrompt("");
    setPickMode("");
    setHelpAnswers({});
    setRecommended([]);
    setDirectCall(false);
    setShowOffer(false);
    setOfferDone(false);
    selectionMode.current = "manual";
    recommendationsAccepted.current = false;
    helpCompletedTracked.current = false;
    // A second journey has to be visible in its own right, so the once-per-journey
    // guards start again too.
    servicesTracked.current = false;
    businessTracked.current = false;
    sectionsTracked.current = new Set();
    completedTracked.current = false;
    quoteTracked.current = "";
    startedTracked.current = false;
    furthest.current = 1;
    setFurthestStage(1);
    go(1);
  };

  const submit = async (form) => {
    setSubmitting(true);
    setSubmitError("");
    try {
      await packageApi.enquiry({ ...form, page_url: window.location.pathname, attribution: getAttribution() });
      setContact({ name: form.full_name, email: form.email });
      setSubmitted(true);
      trackEvent("generate_lead", {
        form_type: "pricing_tool",
        status: quote?.status || "none",
        monthly_net: quote?.totals?.monthly_recurring || 0,
      }, "package_enquiry_submitted");
      localStorage.removeItem(STORE);
    } catch (err) {
      setSubmitError(
        err?.response?.status === 429
          ? "That's a few submissions in a short time. Please try again shortly."
          : "Something went wrong sending that. Please try again, or call us on 01827 780550.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ---------------------------------------------------------------- render
  if (unavailable) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-8 md:p-10 text-center" data-testid="pricing-unavailable">
        <AlertCircle className="text-orange mx-auto" size={26} />
        <h3 className="font-display font-bold text-navy text-xl mt-4">The pricing tool is having a moment</h3>
        <p className="text-sm text-slate-600 mt-3 max-w-lg mx-auto leading-relaxed">
          Our pricing service isn't responding right now, so we can't build you a price online. Tell us what you need and we'll put a
          proper quote together by hand — usually the same working day.
        </p>
        <Link
          to="/contact"
          data-testid="pricing-unavailable-contact"
          className="mt-6 inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold px-8 py-4 rounded-full transition-all hover:-translate-y-0.5"
        >
          Get a quote from us <ArrowRight size={17} />
        </Link>
      </div>
    );
  }

  if (!cat) {
    return (
      <div className="py-20 text-center" data-testid="pricing-loading">
        <Loader2 size={26} className="animate-spin text-orange mx-auto" />
        <p className="text-sm text-slate-500 mt-4">Loading the services…</p>
      </div>
    );
  }

  const withRail = (main, { showProgress = true } = {}) => (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr,300px] gap-8 items-start">
      <div className="min-w-0">{main}</div>
      <div className="min-w-0 lg:sticky lg:top-28">
        <SummaryPanel
          services={summaryServices}
          quote={quote}
          quoting={quoting}
          progress={progress}
          outstanding={localMissing.length}
          emptyText={b.rail_empty}
          showProgress={showProgress}
        />
      </div>
    </div>
  );

  return (
    <div ref={top} data-testid="pricing-journey" data-stage={stage}>
      <div className="mb-8">
        {/* Somebody who went straight to a Discovery Call skipped the middle of
            the journey, so those stages aren't ticked off behind them. */}
        <JourneyProgress stages={STAGES} current={stage} furthest={furthestStage} skipped={directCall && !quote ? [2, 3, 4] : []} onGo={(n) => go(n)} />
      </div>

      {stage === 1 && pickMode === "" && helpEnabled(cfg) && (
        <div className="max-w-2xl" data-testid="pricing-stage1-intro">
          <h2 className="font-display font-extrabold text-navy text-2xl md:text-3xl">{b.step1_heading || "What do you need help with?"}</h2>
          <p className="text-sm md:text-base text-slate-600 mt-2 leading-relaxed">{b.step1_intro}</p>
          <div className="mt-7">
            <HelpChoicePanel help={cfg.help_me_choose || {}} onStart={startHelp} onSkip={() => setPickMode("manual")} />
          </div>
        </div>
      )}

      {stage === 1 && pickMode === "help" && (
        <HelpMeChoose
          help={cfg.help_me_choose || {}}
          questions={helpQuestions(cfg)}
          answers={helpAnswers}
          onAnswer={(key, value) => setHelpAnswers((a) => ({ ...a, [key]: value }))}
          onDone={finishHelp}
          onBack={() => setPickMode("")}
          onDiscovery={helpDiscovery}
        />
      )}

      {stage === 1 && (pickMode === "manual" || !helpEnabled(cfg)) && withRail(
        <div>
          <h2 className="font-display font-extrabold text-navy text-2xl md:text-3xl">{b.step1_heading || "What do you need help with?"}</h2>
          <p className="text-sm md:text-base text-slate-600 mt-2 max-w-2xl leading-relaxed">{b.step1_intro}</p>

          {recommended.length > 0 && (
            <div className="mt-6">
              <Recommendations
                help={cfg.help_me_choose || {}}
                services={services}
                ids={recommended}
                reasons={(cfg.help_me_choose || {}).reasons || {}}
                requiredNames={requiredIds
                  .map((id) => services.find((s) => s.service_id === id)?.name)
                  .filter((n) => n && !recommended.some((id) => services.find((s) => s.service_id === id)?.name === n))}
                standardNames={standardItems.map((s) => s.name)}
                onRedo={() => setPickMode("help")}
                onDiscovery={helpDiscovery}
              />
            </div>
          )}

          {blocked && (
            <div className="mt-6 rounded-2xl border border-orange/40 bg-orange/5 p-5" data-testid="pricing-bookkeeping-required">
              <p className="font-display font-bold text-navy text-sm md:text-base">{bkName} is required</p>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                These are part of our outsourced finance support, so they come with {bkName}:{" "}
                {conflictIds.map((id) => services.find((s) => s.service_id === id)?.name).filter(Boolean).join(", ")}.
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => addBookkeeping()}
                  data-testid="pricing-add-bookkeeping"
                  className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold text-sm px-6 py-3 rounded-full transition-all hover:-translate-y-0.5"
                >
                  Add {bkName} <ArrowRight size={15} />
                </button>
                <button
                  type="button"
                  onClick={() => setSelected((prev) => prev.filter((id) => !conflictIds.includes(id)))}
                  data-testid="pricing-remove-blocked"
                  className="inline-flex items-center gap-2 border border-slate-300 hover:border-navy text-navy font-bold text-sm px-6 py-3 rounded-full transition-colors"
                >
                  Remove these services
                </button>
              </div>
            </div>
          )}

          <div className="mt-7">
            <ServiceCards
              services={services}
              selected={selected}
              onToggle={toggle}
              lockedIds={lockedIds}
              standardItems={standardItems}
              bookkeepingName={bkName}
              promptFor={prompt}
              onAddBookkeeping={(id) => { addBookkeeping(id); }}
              notes={notes}
              recommendedIds={recommended}
              requiredIds={requiredIds}
            />
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <button
              type="button"
              disabled={selected.length === 0 || blocked}
              onClick={leaveServices}
              data-testid="pricing-next-business"
              className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold px-8 py-4 rounded-full duration-300 [transition-property:transform,background-color,opacity] hover:-translate-y-0.5"
            >
              {journey.business.questions.length === 0 ? "Next: your questions" : "Next: tell us about your business"} <ArrowRight size={17} />
            </button>
            <span className="text-xs text-slate-400" data-testid="pricing-selected-count">{chosenCount} selected</span>
          </div>
        </div>,
        { showProgress: false },
      )}

      {stage === 2 && withRail(
        <div>
          <h2 className="font-display font-extrabold text-navy text-2xl md:text-3xl">{b.step2_heading || "Tell us about your business"}</h2>
          <p className="text-sm md:text-base text-slate-600 mt-2 max-w-2xl leading-relaxed">{b.step2_intro}</p>
          <div className="mt-6">
            <QuestionGroups
              groups={journey.business.groups.map((g) => ({ ...g, name: /^general$/i.test(g.name) ? "" : g.name }))}
              answers={answers}
              onChange={onAnswer}
              missingKeys={missingKeys}
              testPrefix="pricing-business"
            />
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button type="button" onClick={() => go(1)} data-testid="pricing-business-back" className="inline-flex items-center gap-2 text-slate-500 hover:text-navy font-semibold text-sm transition-colors">
              Back to services
            </button>
            <button
              type="button"
              onClick={leaveBusiness}
              data-testid="pricing-business-next"
              className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5"
            >
              Next: {sections[firstSectionWithQuestions()]?.service.name || "your price"} <ArrowRight size={16} />
            </button>
          </div>
        </div>,
      )}

      {stage === 3 && withRail(
        <ServiceStage
          sections={sections}
          index={Math.min(serviceIndex, Math.max(sections.length - 1, 0))}
          onGo={(i) => go(3, i)}
          answers={answers}
          onChange={onAnswer}
          missingKeys={missingKeys}
          onBack={() => (serviceIndex > 0 ? go(3, serviceIndex - 1) : go(journey.business.questions.length ? 2 : 1))}
          onNext={leaveSection}
          nextLabel={serviceIndex >= sections.length - 1 ? "See my price" : "Save & next"}
          requiresBookkeepingIds={restrictedIds}
          bookkeepingReady={bkIncluded && !!quote && hasPrice(quote)}
          bookkeepingName={bkName}
        />,
      )}

      {stage === 4 && (
        <div>
          <h2 className="font-display font-extrabold text-navy text-2xl md:text-3xl mb-6">{b.step3_heading || "Your indicative price"}</h2>
          <PriceStage
            quote={quote}
            quoting={quoting}
            error={quoteError}
            sections={sections}
            idsByName={nameToId}
            onEdit={(target) => (target === "business" ? go(2) : go(3, target))}
            onBack={() => go(3, Math.max(sections.length - 1, 0))}
            onContinue={() => go(5)}
            onBook={() => go(5)}
            onStartOver={startOver}
            disclaimer={cfg.disclaimer}
            summaryHeading={b.summary_heading}
          />
        </div>
      )}

      {stage === 5 && (
        <div className="max-w-2xl">
          {submitted ? (
            <>
              <div className="rounded-2xl border border-teal/30 bg-teal/5 p-5 flex items-start gap-3 mb-8" data-testid="pricing-submitted">
                <CheckCircle2 size={20} className="text-teal shrink-0 mt-0.5" />
                <p className="text-sm text-navy">
                  {b.submitted_message || "Got it — thank you. Your package and all your answers are with us."}
                  {contact?.name ? ` Speak soon, ${contact.name.split(" ")[0]}.` : ""}
                </p>
              </div>
              <DiscoveryBooking prefill={contact} heading={b.booking_heading} intro={b.booking_intro} />
            </>
          ) : directCall ? (
            <>
              <h2 className="font-display font-extrabold text-navy text-2xl md:text-3xl">
                {quote ? "Let's talk your price through" : "Let's work it out together"}
              </h2>
              <p className="text-sm md:text-base text-slate-600 mt-2 leading-relaxed">
                {quote
                  ? "Pick a time that suits you. We'll go through the package you've built, confirm the figure and answer anything it raised."
                  : "No need to choose anything first. Pick a time that suits you and we'll talk through what the business needs."}
              </p>
              <div className="mt-6">
                <DiscoveryBooking
                  heading={b.booking_heading}
                  intro="Pick a time that suits you. We'll talk through what the business needs and where we'd start — no pressure, no obligation."
                />
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-5">
                <button
                  type="button"
                  onClick={() => setDirectCall(false)}
                  data-testid="pricing-direct-details"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 duration-200 [transition-property:color] hover:text-navy"
                >
                  Send us your details instead
                </button>
                <button
                  type="button"
                  onClick={() => { setDirectCall(false); setPickMode("manual"); go(1); }}
                  data-testid="pricing-direct-back"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 duration-200 [transition-property:color] hover:text-navy"
                >
                  Back to the services
                </button>
              </div>
            </>
          ) : (
            <>
              <h2 className="font-display font-extrabold text-navy text-2xl md:text-3xl">{b.step4_heading || "Let's talk it through"}</h2>
              <div className="mt-6">
                <PackageContact onSubmit={submit} busy={submitting} error={submitError} heading={b.contact_heading} intro={b.contact_intro} />
              </div>
              <button type="button" onClick={() => go(quote ? 4 : 1)} data-testid="pricing-details-back" className="mt-8 inline-flex items-center gap-2 text-slate-500 hover:text-navy font-semibold text-sm transition-colors">
                {quote ? "Back to my price" : "Back to the services"}
              </button>
            </>
          )}
        </div>
      )}

      {showOffer && <WalkawayOffer offer={offer} onBook={bookFromOffer} onDismiss={dismissOffer} />}
    </div>
  );
}
